import { useEffect, useMemo, useRef, createContext, useState } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import MyAccountContentHeader from '../MyAccountContentHeader/MyAccountContentHeader';
import AppInstallBanner from '../../../../components/AppInstallBanner/AppInstallBanner';
import MyAccountOrderDetailRow from '../MyAccountOrderDetailRow/MyAccountOrderDetailRow';
import DownloadActionButton from './DownloadActionButton';
import MyAccountOrderNotes from '../MyAccountOrderNotes';
import './MyAccountOrderDetails.css';
import * as translator from '../../../../utils/translator';
import SubmitButton from '../../../../components/SubmitButton/SubmitButton';
import { formatTimestamp, formatDateStamp, getLocaleFormatOptions } from '../../../../utils/timestampFormatter';
import PharmacySelection from './PharmacySelection/PharmacySelection';
import * as http from '../../../../utils/http';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS, ORDER_FLOWS } from '../../../../utils/consts';
import OrderFeedbackForm from '../../Forms/OrderFeedbackForm';
import ThanksForFeedback from '../MyAccountOrderFeedBack/ThanksForFeedbackWidget';
import Timeline from '../../../../components/Timeline/Timeline';
import CopyToClipboard from '../../../../components/copyToClipboard/copyToClipboard';
import MyAccountOrderDisclaimer from '../MyAccountOrderDisclaimer/MyAccountOrderDisclaimer';
import MyAccountOrderDetailsSupport from '../MyAccountOrderDetailsSupport/MyAccountOrderDetailsSupport';
import Popup from '../../../../components/Popup/Popup';
import { myAccountTimelineCheckpointsCreator } from '../../../../components/Timeline/TimelineProcessor';
import { calculateColorAndDescription } from '../MyAccountOrderDisclaimer/MyAccountOrderDisclaimerPreProcess';
import AnimatedPopup from '../../../../components/AnimatedPopup/AnimatedPopup';
import RescheduleVideoConsultationModal from '../../../../components/RescheduleVideoConsultationModal';
import MyAccountChangeAddress from '../MyAccountChangeAddress/MyAccountChangeAddress';
import MyAccountTestKitResults from '../MyAccountTestKitResults/MyAccountTestKitResults';
import * as consts from '../../../../utils/consts';
import { TWENTY_FOUR_HOURS_MS, FIFTEEN_MINUTES_MS, ALTERNATIVE_TREATMENT_OFFERED } from '../../../../utils/consts';
import { buildOrderBundleProps } from '../../../../components/Modals/AlternativeOfferModal/doctorAlternativeTracking';
import useIsMobile from '../../../../customHooks/useIsMobile';
import MyAccountPendingActions from '../MyAccountPendingActions/MyAccountPendingActions';
import ReorderActions from '../ReorderActions/ReorderActions';
import { ReorderTrackingContext, WlReorderSubscriptionOffer } from '../../../Reorder/reorderTypes';
import { assignWlReorderVariantForOffer, WlReorderVariant } from '../../../../utils/wlReorderSubscriptionExperiment';
const DEPRACATED_PAYMENT_METHOD_CODE = ['swift', 'iban'];
const cannaleo_pre_selected_flows = [ORDER_FLOWS.CANNALEO_PRE_SELECTED, ORDER_FLOWS.FLOWZZ_PRE_SELECTED];
// Statuses that display "Doctor is reviewing your information" in the user-facing status label.
const DOCTOR_REVIEW_STATUSES = ['wait_prescription', 'wait_prescription_generation', 'wait_sign_confirmation',
	'processing_wait_sign_confirmation', 'wait_money_capture', 'ready_to_sign', 'clarified_by_support'];

type DoctorAlternativeSuggestion = {
	originalProductTitle: string;
	originalProductPrice: number;
	suggestedProductTitle: string;
	suggestedProductPrice: number;
};

type OrderDetailsContextType = {
	products: any;
	orderStatus: string;
	mention: string;
	doctorAlternativeSuggestions: DoctorAlternativeSuggestion[];
	hashID?: string;
	groupHashID?: string;
	categoryShortcut?: string;
};

export type OrderData = {
	flow: string;
	/** null = not in the WL reorder test. */
	wlSubscriptionOffer?: WlReorderSubscriptionOffer | null;
	reorderTrackingContext?: ReorderTrackingContext | null;
	feedbackRating: number;
	pharmacyRejectedAt: string | null;
	pharmacyApprovedAt: string | null;
	selectedEPharmacyID?: number | null;
	pharmacyAlternativeOffer: any;
	cartView: {
		cart: {
			self_pickup_pharmacy_id: number | null;
			treatment_fee: number;
			custom_shipping_fee: number;
			only_prescription: number;
			locale_id: string;
			flow: string;
			applied_subscription_discount: number;
			bonus_value: number;
			bonus_id: number | null;
		};
		products: [
			{
				id: number;
				order_id: number;
				product_id: number;
				product_item_id: number;
				catalog_id: number;
				quantity: number;
				price: number;
				price_determined_by_stock_id: number;
				treatment_fee: number;
				is_prescription_drug: number;
				created_at: string;
				updated_at: string;
				deleted_at: string | null;
				product_changed: number;
				type: string;
				catalog_price_override_id: number | null;
				item_price: number;
				title: string;
				titleView: string;
				quantityStr: string;
				imageSrc: string;
				application_notes: string;
				subscriptionPlans: any[];
				catalogPage: string;
				cannabisCatalogProductProperties: {
					cbd: number;
					effects: string[];
					genetic: string;
					strain: string;
					terpenes: string[];
					thc: number;
					type: string;
				};
			}
		];
	};
	isCannabisOrder: boolean;
	hashID: string;
	groupHashID?: string;
	categoryShortcut?: string;
	orderDate: string;
	orderShipping: {
		order_id: number;
		method_id: number;
		tracking_url: string | null;
		tracking_id: string | null;
		contractor: string | null;
		shipping_id: number | null;
		sent_at: string | null;
	};
	orderTimekitBooking?: {
		id: number;
		order_id: number;
		timekit_booking_id: string;
		timekit_reservation_id: string;
		start_meeting_link?: string | null;
		join_meeting_link?: string | null;
		status: "reserved" | "pending" | "ongoing" | "completed" | "cancelled_by_customer" | "cancelled_by_doctor" | "cancelled_by_system";
		start: string;
		end: string;
		day_before_reminder_sent: boolean;
		created_at?: string;
		updated_at?: string;
	} | null;
	doctorName: string | null;
	status: string;
	doctorAlternativeAccepted?: boolean;
	doctorAlternativeRejected?: boolean;
	rejectReason: string;
	rejectReasonCode?: string;
	title: string;
	shortTitle: string;
	pillQuantity: string;
	prices: {
		treatmentFee: string;
		drug: string;
		shipping: string;
		cannabisIdFee: string;
		total: string;
	};
	paymentMethod: {
		paymentMethodTitle: string;
		isShowPaymentButton?: boolean;
		shouldShowPaymentButton?: boolean;
		hashID: string;
		phone: string;
	};
	image: string;
	prescription: {
		id: number;
		question: string;
		answer: string;
		question_type: string;
	}[];
	orderType: string;
	shippingMethod: string;
	shippingCompanyCode?: string;
	expressTier?: string | null;
	isExternalPharmacy: number;
	allowEPharmacyChange: boolean;
	address: string[];
	deliveryAddress: string[];
	orderDeliveryDetails: {
		first_name: string;
		last_name: string;
		address: string;
		zip: string;
		city: string;
		country_id: string;
		phone: string;
		company: string;
	};
	pharmacyDetails: string[];
	applicationNotes: string | null;
	mention: string;
	isOnlineVD: string;
	catalogID: number;
	orderPrescriptionDetails: any[];
	orderPrescription: {
		id: number;
		order_id: number;
		doctor_id: number | null;
		driver: 'string';
		confirmed: 1 | 0;
		application_notes: string;
		warning_label: string;
		auto_processing: string | null;
		reject_reason: string;
		reject_reason_code: string | null;
		is_outdated: 1 | 0;
		mark_approved_settings: string | null;
		process_approving_after: string | null;
		diagnosis: string;
		created_at: string;
		updated_at: string;
		assigned_at: null;
		approved_at: null;
		viewed_at: null;
		rejected_at: null;
		rejection_count: 0;
		is_high_priority: 0;
	};
	notifications: number;
	status_reason?: string;
	sub_status?: string | null;
	isAvailableForReorder: boolean;
	createdAt: string;
	paidAt: string | null;
	orderID: number;
	isPaid: boolean;
	hasPrescription: boolean;
	shouldShowDownloadButton: boolean;
	isCannabisCardRequired: 1 | 0;
	haveFeedback: boolean;
	sentAt: string | null;
	sentToPharmacyAt: string | null;
	deliveredAt: string | null;
	last_status: string;
	last_status_update: string;
	checkpointDates: string[];
	isSelfPickup: boolean;
	hasProbatixPdfAttachment?: boolean | null;
	resultsURL?: string | null;
	isProbatixOrder?: boolean;
	testIncludes?: string;
	testSampleType?: 'blood' | 'urine' | 'stool' | 'vaginal_swab' | null;
	doctorAlternativeSuggestions?: DoctorAlternativeSuggestion[];
};
export const OrderDetailsContext = createContext<OrderDetailsContextType>({ products: null, orderStatus: '', mention: '', doctorAlternativeSuggestions: [] });

const MyAccountOrderDetails = () => {
	const { language, setError, customer, country, setStickyNotificationDetails, setRefreshData, setLoading, refreshData, setCurrentGlobalModalName, asideBarData } = useOutletContext<any>();
	const { order_hash_id } = useParams();
	const url = new URL(window.location.href);
	const queryParams = new URLSearchParams(window.location.search);
	const action = queryParams.get('re');
	const [orderData, setOrderData] = useState<OrderData | null>(null);
	// undefined until the details response answers, which keeps W5 hidden
	const [appInstallPlacements, setAppInstallPlacements] = useState<string[] | undefined>(undefined);
	const [wlReorderVariant, setWlReorderVariant] = useState<WlReorderVariant | null>(null);
	const [showCancelVideoConsultationPopup, setShowCancelVideoConsultationPopup] = useState(false);
	const [showRescheduleVideoConsultationModal, setShowRescheduleVideoConsultationModal] = useState(false);
	const [showRedeliveryAddressPopup, setShowRedeliveryAddressPopup] = useState(false);
	const [isRedeliveryCloseable, setIsRedeliveryCloseable] = useState(false);
	const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
	const [isDownloadingPrescription, setIsDownloadingPrescription] = useState(false);
	const isSchengenFlow = orderData?.flow === ORDER_FLOWS.SCHENGEN;
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	
	const entryEventFired = useRef(false);

	// Track entry point when customer lands on an order with a pending doctor alternative.
	useEffect(() => {
		if (!orderData || entryEventFired.current) return;
		if (orderData.status !== ALTERNATIVE_TREATMENT_OFFERED) return;

		const queryParams = new URLSearchParams(window.location.search);
		const utmSource = queryParams.get('utm_source');
		const utmMedium = queryParams.get('utm_medium');

		let entryMethod = 'My Account direct';
		if (utmMedium === 'doctor_alternative') {
			if (utmSource === 'email') entryMethod = 'Email CTA';
			else if (utmSource === 'whatsapp') entryMethod = 'WhatsApp CTA';
		}

		MainTracker.track('custom', 'Doctor alternative My Account entry', {
			...buildOrderBundleProps({
				hashID: orderData.hashID,
				groupHashID: orderData.groupHashID,
				status: orderData.status,
				category: orderData.categoryShortcut,
			}),
			'Entry Method': entryMethod,
		});

		entryEventFired.current = true;
	}, [orderData]);

	const showConsultationBookingLink = useMemo(() => {
		if (!orderData?.orderTimekitBooking || orderData?.status === 'cancelled') return false;
		const startTime = new Date(orderData?.orderTimekitBooking.start).getTime();
		const now = new Date().getTime();
		return now < startTime + FIFTEEN_MINUTES_MS && now < startTime + FIFTEEN_MINUTES_MS;
	  }, [orderData?.orderTimekitBooking]);


	const paymentMethodWithoutIbanAndSwift = Object.fromEntries(
		Object.entries(orderData?.paymentMethod ?? {}).filter(([key]) => !DEPRACATED_PAYMENT_METHOD_CODE.includes(key))
	);

	//used for reship, switch to DHL delivery, refund, or cancel
	useEffect(() => {
		window.scrollTo(0, 0);
		setLoading(true);
		http
			.getFromServer(`${country}/account/orders/${order_hash_id}/details`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				// Buckets devices that reach Details before My Orders; sticky otherwise.
				setWlReorderVariant(assignWlReorderVariantForOffer(response.data?.orderData?.wlSubscriptionOffer));
				setOrderData(response.data?.orderData);
				setAppInstallPlacements(response.data?.appInstallPlacements);
			})
			.catch((error) => {
				window.clientLogger.error('Error in MyAccountOrderDetails: ', error);
				setError(500);
			})
			.finally(() => {
				setLoading(false);
			});

		if (action) {
			MainTracker.track('custom', 'Customer initiated reship or cancel', { action });
			http
				.postToServer(
					'action',
					{
						action,
						hashID: order_hash_id,
					},
					[http.MIDDLEWARES.CUSTOMER_ID]
				)
				.then((response) => {
					const isCloseable = queryParams.get('closeable') === '1';
					queryParams.delete('re');
					queryParams.delete('closeable');
					window.history.replaceState(null, '', `${url.pathname}?${queryParams.toString()}`);

					if (response.data.showAddressChangePopup) {
						setIsRedeliveryCloseable(isCloseable);
						setShowRedeliveryAddressPopup(true);
						return;
					}

					setStickyNotificationDetails((currentDetails: any) => ({
						show: true,
						title: translator.translate(language, 'my_account', `stickyNotifications/success/thank_you`),
						content: translator
							.translate(language, 'my_account', `stickyNotifications/success/${response.data.messageCode}`)
							.replace(':order_id', order_hash_id),
						level: 'info',
						showInfoIcon: false,
						refresh: !currentDetails.refresh,
					}));

					const { shouldRefreshData } = response.data;

					if (shouldRefreshData) {
						// Trigger data refresh in parent layout
						setRefreshData && setRefreshData((prev: boolean) => !prev);
					}
				})
				.catch((error) => {
					queryParams.delete('re');
					queryParams.delete('closeable');
					window.history.replaceState(null, '', `${url.pathname}?${queryParams.toString()}`);

					setStickyNotificationDetails((currentDetails: any) => ({
						show: true,
						title: '',
						content: translator.translate(language, 'my_account', `stickyNotifications/error/actionDenied`).replace(':order_id', order_hash_id),
						level: 'error',
						showInfoIcon: false,
						refresh: !currentDetails.refresh,
					}));
				});
		}
	}, [refreshData]);

	// Handle hash fragment scrolling to feedback form
	useEffect(() => {
		if (window.location.hash === '#order-details-feedback') {
			const feedbackElement = document.getElementById('order-details-feedback');
			if (feedbackElement) {
				setTimeout(() => {
					feedbackElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}, 100); // Small delay to ensure the component is fully rendered
			}
		}
	}, []);

	const statusData = useMemo(() => {
		if (!orderData) return { class: '', title: '', description: '', showIcon: false };
		return calculateColorAndDescription(orderData, language, 'details');
	}, [orderData]);

	if (!order_hash_id || !country) {
		return setError(400);
	}
	if (!orderData) {
		return null;
	}
	const data = JSON.parse(JSON.stringify(orderData));
	const orderNotesString = data?.orderType == 'prescription_only' ? 'orderNotesPrescriptionOnly' : 'orderNotes';

	delete data?.['prices']?.['treatmentFeeDiscount']; // we dont need this key in price display
	if (!data?.isCannabisCardRequired) {
		delete data?.['prices']?.['cannabisIdFee'];
	}

	// Resolve a localized label for the shipping row. Express tiers (urbify/uber) and
	// self_pickup map to MyOrders/details/shippingMethodLabels/<key>; unmapped carriers
	// (DHL, UPS, …) fall back to data['shippingMethod'], the backend's display title.
	// The key is built from the carrier code — data['shippingMethod'] is Ops-editable free text.
	const getShippingMethodLabel = () => {
		const company = (data['shippingCompanyCode'] || '').toLowerCase();
		const tier = data['expressTier'];
		const key = tier ? `${company}_${tier}` : company;
		const label = key
			? translator.translate(language, 'my_account', `MyOrders/details/shippingMethodLabels/${key}`)
			: null;
		return label || data['shippingMethod'];
	};

	const isProbatixOrder = orderData?.isProbatixOrder;
	const testSampleType = orderData?.testSampleType ?? 'unknown';
	const products = (data?.orderType == 'prescription_only' ? [] : data?.['cartView']?.['products']) ?? [];
	const productsView = products.map((product: { titleView: string; title: string; imageSrc: string; type: string }) => {
		return { image: product.imageSrc, title: isProbatixOrder ? product.title : product.titleView };
	});

	// Save a blob response from the server as a downloaded file.
	const saveBlob = (response: any, fileName: string) => {
		const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/pdf' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', fileName);
		document.body.appendChild(link);
		link.click();
		link.parentNode?.removeChild(link);
		window.URL.revokeObjectURL(url);
	};

	const handleDownloadInvoice = () => {
		const orderID = data?.orderID;
		const invoiceDate = data?.paidAt ? new Date(data.paidAt) : new Date();
		const datePart = `${invoiceDate.getFullYear()}${String(invoiceDate.getMonth() + 1).padStart(2, '0')}${String(invoiceDate.getDate()).padStart(2, '0')}`;
		const fileName = `Invoice_${orderID}_${datePart}.pdf`;
		setIsDownloadingInvoice(true);
		http
			.getFromServer(`${country}/account/orders/${order_hash_id}/invoice/download`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.GENERATE_FILE])
			.then((response: any) => {
				saveBlob(response, fileName);
				MainTracker.track('custom', 'Invoice_Downloaded', {
					order_id: orderID,
					invoice_file_name: fileName,
					order_status: data?.status,
				});
			})
			.catch((error: any) => {
				window.clientLogger.error('Failed to download invoice:', error);
				MainTracker.track('custom', 'Download_Failed', {
					order_id: orderID,
					download_type: 'invoice',
					error_reason: error?.message || 'unknown',
				});
			})
			.finally(() => setIsDownloadingInvoice(false));
	};

	const handleDownloadPrescription = () => {
		const orderID = data?.orderID;
		const fileName = `Prescription_${orderID}.pdf`;
		setIsDownloadingPrescription(true);
		http
			.getFromServer(`${country}/account/orders/${order_hash_id}/prescription/download`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.GENERATE_FILE])
			.then((response: any) => {
				saveBlob(response, fileName);
				MainTracker.track('custom', 'Prescription_Downloaded', {
					order_id: orderID,
					prescription_file_name: fileName,
				});
			})
			.catch((error: any) => {
				window.clientLogger.error('Failed to download prescription:', error);
				MainTracker.track('custom', 'Download_Failed', {
					order_id: orderID,
					download_type: 'prescription',
					error_reason: error?.message || 'unknown',
				});
			})
			.finally(() => setIsDownloadingPrescription(false));
	};

	// Stacked download actions, rendered to the right of the Order ID row. Each button is gated
	// independently: invoice only for paid orders without a refund reconciliation diff, prescription
	// only when a recipe PDF exists.
	const orderActions = ((data?.isPaid && data?.shouldShowDownloadButton) || data?.hasPrescription) ? (
		<div className="order-detail-actions">
			{data?.isPaid && data?.shouldShowDownloadButton && (
				<DownloadActionButton
					id="download-invoice-button"
					label={translator.translate(language, 'my_account', 'MyOrders/details/downloadInvoice')}
					onClick={handleDownloadInvoice}
					loading={isDownloadingInvoice}
				/>
			)}
			{data?.hasPrescription && (
				<DownloadActionButton
					id="download-prescription-button"
					label={translator.translate(language, 'my_account', 'MyOrders/details/downloadPrescription')}
					onClick={handleDownloadPrescription}
					loading={isDownloadingPrescription}
				/>
			)}
		</div>
	) : null;

	const handleNewOrder = () => {
		navigate({ pathname: `/select_treatment/${country}/${language}/${data.mention}`, search: `?skipQuestionnaire=true` });
	};
	const checkpoints = orderData ? myAccountTimelineCheckpointsCreator(orderData, language) : [];

	// Split prices for test lab desktop view: separate "Price" (all except total) from "Total amount" (total only)
	const probatixSubPrices = data?.['prices'] ?? {};
	const probatixTotalPrice = data?.['prices']?.total ?? '';

	// HL-11177: cancelled consultations must not render as upcoming appointments.
	const orderTimekitBooking = orderData?.orderTimekitBooking;
	const CANCELLED_BOOKING_STATUSES = ['cancelled_by_customer', 'cancelled_by_doctor', 'cancelled_by_system'];
	// ...except if the order is awaiting customer booking selection (WAIT_CUSTOMER_BOOKING_SELECTION), 
	// always show the consultation block so the customer can pick/reschedule a slot, regardless of the previous booking's cancellation status.
	const needsRebooking = orderData?.status === consts.WAIT_CUSTOMER_BOOKING_SELECTION;
	const isBookingCancelled = CANCELLED_BOOKING_STATUSES.includes(String(orderTimekitBooking?.status));
	const shouldShowConsultationDetails = !!orderTimekitBooking && (!isBookingCancelled || needsRebooking);

	// Function to render consultation details content
	const renderConsultationDetails = () => {
		if (!shouldShowConsultationDetails) {
			return null;
		}

		const doctorName = orderData?.doctorName;
		
		const localeOptions = getLocaleFormatOptions(`${language.toLowerCase()}-${country.toUpperCase()}`);
		
		const consultationDate = formatDateStamp(orderTimekitBooking.start, { 
			showDay: false,
			...localeOptions
		});
		
		// Format time - show range for preferred time bookings (no doctor selected)
		const formatConsultationTime = () => {
			// Check if it's a preferred time booking (no doctor_id means preferred time)
			const isPreferredTimeBooking = !(orderTimekitBooking as any).doctor_id;
			if (isPreferredTimeBooking && orderTimekitBooking.end) {
				// Show time range for preferred time bookings
				const startTime = formatTimestamp(orderTimekitBooking.start, { 
					showDate: false, 
					showTime: true,
					...localeOptions
				});
				const endTime = formatTimestamp(orderTimekitBooking.end, { 
					showDate: false, 
					showTime: true,
					...localeOptions
				});
				return `${startTime}-${endTime}`;
			}
			// Show only start time for doctor-selected bookings
			return formatTimestamp(orderTimekitBooking.start, { 
				showDate: false, 
				showTime: true,
				...localeOptions
			});
		};
		const consultationTime = formatConsultationTime();

		// Check if consultation is within 24 hours (for enabling/disabling reschedule/cancel)
		const consultationTime24h = new Date(orderTimekitBooking.start).getTime() - new Date().getTime();
		const isWithin24Hours = consultationTime24h < TWENTY_FOUR_HOURS_MS;
		
		return (
			<div className="order-details-consultation-content">
				{!isBookingCancelled && <div className="order-details-consultation-info">
					<div className="order-details-consultation-info-row">
						<span className="order-details-consultation-label">
							{translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/doctor_label')}
						</span>
						<span className="order-details-consultation-value">{doctorName}</span>
					</div>
			
					<div className="order-details-consultation-info-row">
						<span className="order-details-consultation-label">
							{translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/date_label')}
						</span>
						<span className="order-details-consultation-value">{consultationDate}</span>
					</div>
			
					<div className="order-details-consultation-info-row">
						<span className="order-details-consultation-label">
							{translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/time_label')}
						</span>
						<span className="order-details-consultation-value">{consultationTime}</span>
					</div>
				
					{(orderTimekitBooking.status === 'pending' || orderTimekitBooking.status === 'ongoing') && showConsultationBookingLink &&
						<a
							id="join-video-call-btn"
							href={orderTimekitBooking.join_meeting_link ?? ""}
							target="_blank"
							rel="noopener noreferrer"
							className="order-details-consultation-link"
						>
							{translator.translate(language, 'my_account', 'MyOrders/details/joinVideoCall')}
						</a>
					}
				</div>}

				{(orderTimekitBooking.status === 'pending' || needsRebooking) && (
					<>
						<div className="order-details-consultation-actions">
							<SubmitButton
								id="reschedule-consultation-btn"
								placeholder={translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/reschedule')}
								fontsize="15px"
								isEnabledState={!isWithin24Hours || needsRebooking}
								onSubmit={(e: React.MouseEvent) => {
									if (e.stopPropagation) e.stopPropagation();
									setShowRescheduleVideoConsultationModal(true);
								}}
							/>

							<SubmitButton
								id="cancel-consultation-btn"
								placeholder={translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/cancel_meeting')}
								fontsize="15px"
								className={`${(orderData.status === consts.WAIT_VIDEO_CONSULTATION && !isWithin24Hours) || needsRebooking ? 'cancel-consultation-btn' : 'pointer-events-none select-none opacity-30'}`}
								onSubmit={(e: React.MouseEvent) => {
									if (e.stopPropagation) e.stopPropagation();
									setShowCancelVideoConsultationPopup(true);
									MainTracker.track('custom', 'Click to cancel videoconsultation');
									MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': 'Cancel videoconsultation order'})
								}}
							/>

							<RescheduleVideoConsultationModal
								country={country!}
								language={language!}
								shouldShow={showRescheduleVideoConsultationModal}
								onClose={() => setShowRescheduleVideoConsultationModal(false)}
								catalogID={orderData.catalogID}
								hashID={orderData.hashID}
								shouldAlternateBetweenTitles={false}
								onSuccess={() => {
									window.location.reload(); // Refresh order data to reflect reschedule
									setStickyNotificationDetails((currentDetails: any) => ({
										show: true,
										title: translator.translate(language, "default", "StickyNotification/video_consultation_success_title") as string,
										content: translator.translate(language, "default", "StickyNotification/video_consultation_reschedule_success") as string,
										level: 'success',
										showInfoIcon: true,
										refresh: !currentDetails.refresh,
									}));
								}}
								onFailure={() => {
									setStickyNotificationDetails((currentDetails: any) => ({
										show: true,
										level: "error",
										showInfoIcon: true,
										title: translator.translate(language, "default", "StickyNotification/danger_title") as string,
										content: translator.translate(language, "default", "StickyNotification/video_consultation_reschedule_error") as string,
										refresh: !currentDetails.refresh,
									}));
								}}
								getEndPointURL={`account/reschedule-video-consultation`}
								postEndPointURL={`account/reschedule-video-consultation/${orderData.hashID}`}
							/>

							<AnimatedPopup
								isVisible={showCancelVideoConsultationPopup}
								onClose={() => {
									setShowCancelVideoConsultationPopup(false);
									MainTracker.track('custom', 'Decision on Cancel videoconsultation', {'Decision': 'No, keep order'});
								}}
								title={translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/cancel_meeting')}
								shouldBackgroundClickClose={true}
							>
								<div className='flex flex-col justify-center items-center gap-4'>
									<div className="text-center text-[22px] font-medium">
										{translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/cancel_popup_content')}
									</div>
									<div className="flex flex-col justify-center items-center pt-2">
										<button
											onClick={() => setShowCancelVideoConsultationPopup(false)}
											className="max-w-full border-none bg-[#0A9281] text-white font-semibold text-[16px] rounded-[8px] py-4 px-6 mb-2 cursor-pointer hover:opacity-80"
											style={{ boxShadow: '0px 2px 8px 0px #0000000D' }}
										>
											{translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/cancel_popup_keep')}
										</button>
										<button
											onClick={async () => {
												setShowCancelVideoConsultationPopup(false);
												setLoading(true);
												try {
													await http.postToServer('account-update/video-consultation/cancel', {
														hashID: data.hashID
													}, [http.MIDDLEWARES.CUSTOMER_ID]);
													window.location.reload(); // Refresh order data to reflect cancellation
													setStickyNotificationDetails((currentDetails: any) => ({
														show: true,
														level: "success",
														showInfoIcon: true,
														title: translator.translate(language, "default", "StickyNotification/video_consultation_success_title") as string,
														content: translator.translate(language, "default", "StickyNotification/video_consultation_cancel_success") as string,
														refresh: !currentDetails.refresh,
													}));
												} catch (error) {
													setStickyNotificationDetails((currentDetails: any) => ({
														show: true,
														level: "error",
														showInfoIcon: true,
														title: translator.translate(language, "default", "StickyNotification/danger_title") as string,
														content: translator.translate(language, "default", "StickyNotification/video_consultation_cancel_error") as string,
														refresh: !currentDetails.refresh,
													}));
												} finally {
													MainTracker.track('custom', 'Decision on Cancel videoconsultation', {'Decision': 'Yes, cancel'});
													setLoading(false);
												}
											}}
											className="max-w-full text-[#747474] font-medium text-[16px] underline underline-offset-2 cursor-pointer"
											style={{ background: 'none', border: 'none', marginTop: '8px' }}
										>
											{translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/cancel_popup_confirm')}
										</button>
									</div>
								</div>
							</AnimatedPopup>
						</div>
					
						<p className="order-details-consultation-restriction-note">
							{translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/reschedule_cancel_note')}
						</p>
					</>
				)}
			</div>
		);
	};

	if (!orderData) {
		return <></>;
	}

	return (
		<OrderDetailsContext.Provider value={{ products: data.cartView?.products ?? [], orderStatus: orderData.status, mention: data.mention ?? '', doctorAlternativeSuggestions: data.doctorAlternativeSuggestions ?? [], hashID: data.hashID, groupHashID: data.groupHashID, categoryShortcut: data.categoryShortcut }}>
			<div className="account-content">
				{queryParams.get('showRedirectModals') === 'true' ? (
					<Popup
						title={translator.translate(language, 'my_account', 'PopupFromPayment/title')}
						description={translator.translate(language, 'my_account', 'PopupFromPayment/text')}
						primaryButtonText={translator.translate(language, 'my_account', 'PopupFromPayment/stay')}
						secondaryButtonText={translator.translate(language, 'my_account', 'PopupFromPayment/new_order')}
						handleSecondaryButton={handleNewOrder}
					/>
				) : null}

			{showRedeliveryAddressPopup && orderData.orderDeliveryDetails && (
				<MyAccountChangeAddress
					isOpen={showRedeliveryAddressPopup}
					language={language}
					country={country}
					orderDeliveryDetails={orderData.orderDeliveryDetails}
					orderHashID={data.hashID}
					closeable={isRedeliveryCloseable}
					onClose={() => isRedeliveryCloseable && setShowRedeliveryAddressPopup(false)}
					onSuccess={() => {
						setShowRedeliveryAddressPopup(false);
						setStickyNotificationDetails((currentDetails: any) => ({
							show: true,
							title: translator.translate(language, 'my_account', 'stickyNotifications/success/thank_you'),
							content: translator.translate(language, 'my_account', 'stickyNotifications/success/reship_change_address') || 'Your address has been updated and the order is being processed.',
							level: 'success',
							showInfoIcon: false,
							refresh: !currentDetails.refresh,
						}));
						setRefreshData((prev: boolean) => !prev);
					}}
					onError={() => {
						setShowRedeliveryAddressPopup(false);
						setStickyNotificationDetails((currentDetails: any) => ({
							show: true,
							title: translator.translate(language, 'my_account', 'stickyNotifications/error/title') || 'Error',
							content: translator.translate(language, 'my_account', 'stickyNotifications/error/reship_change_address') || 'Failed to update address. Please try again or contact support.',
							level: 'error',
							showInfoIcon: true,
							refresh: !currentDetails.refresh,
						}));
					}}
				/>
			)}

			<MyAccountContentHeader
				title={`${data['hashID']}`}
				titleFurnish={<CopyToClipboard textToCopy={data['hashID']} />}
				description={
					(orderData.doctorAlternativeAccepted || orderData.doctorAlternativeRejected) &&
					DOCTOR_REVIEW_STATUSES.includes(orderData.status)
						? ''
						// A specific address_verify sub-status has its own text, otherwise the generic status text is shown.
						: (orderData.status === consts.ADDRESS_VERIFY && orderData.sub_status && translator.translate(language, 'my_account', `MyOrders/status/address_verify_sub_statuses/${orderData.sub_status}`)) ||
							translator.translate(
								language,
								'my_account',
								orderData.status === 'sent_to_pharmacy' && data.orderType !== 'prescription_only'
									? 'MyOrders/details/timelineStatus/sent_to_pharmacy'
									: `MyOrders/status/${orderData.status}`
							)
				}
					descriptionClass={`order-status ${statusData.class}`}
				/>

				{![consts.CANCELLED, consts.CANCELLED_BY_CUSTOMER].includes(orderData.status) && (
					// A cancelled order has nothing to track in the app.
					<AppInstallBanner
						desktopPlacementId="W5"
						mobilePlacementId="W5"
						language={language}
						country={country}
						customerUuid={asideBarData?.uuid}
						isPairedToMobileApp={asideBarData?.isPairedToMobileApp}
						allowedPlacements={appInstallPlacements}
						orderHash={order_hash_id}
						className="account-app-install-banner"
					/>
				)}

				<Timeline customClass={data.orderType == 'prescription_only' ? 'regular' : 'premium'} checkpoints={checkpoints} />


				{statusData.description ? (
					<MyAccountOrderDisclaimer
						statusColor={orderData.rejectReason ? 'danger' : statusData.class}
						description={statusData.description}
						title={statusData.title}
						showIcon={statusData.showIcon}
						cardClass="detail-disclaimer-card"
						actionButton={
							orderData.status === consts.ALTERNATIVE_TREATMENT_OFFERED && data.doctorAlternativeSuggestions?.length > 0 ? (
								<button
									className="actionButton"
									onClick={() => {
										const originalProductPrice = data.doctorAlternativeSuggestions?.[0]?.originalProductPrice;
										MainTracker.track('custom', 'My account show doctor alternative', {
											hashID: data.hashID,
											groupHashID: data.groupHashID,
											status: orderData.status,
											category: data.categoryShortcut,
											price: originalProductPrice,
										});
										setCurrentGlobalModalName({
											type: 'DOCTOR_ALTERNATIVE_OFFER',
											orderProducts: products.map((p: any) => ({ order_id: orderData.orderID })),
											doctorAlternativeSuggestions: data.doctorAlternativeSuggestions,
											orderBundle: {
												hashID: data.hashID,
												groupHashID: data.groupHashID,
												orderStatus: orderData.status,
												categoryShortcut: data.categoryShortcut,
											},
										});
									}}
								>
									{translator.translate(language, 'my_account', 'MyOrders/details/alternative_offer_modal/show_alternatives')}
								</button>
							) : undefined
						}
					/>
				) : null}

				{orderData.status === 'address_verify' &&
					['reshipment requested by customer', 'reshipment requested by customer timeout'].includes(orderData.status_reason ?? '') && (
					<div className="card warning detail-disclaimer-card" id="my-account-details-disclaimer">
						<div className="contentWrapper">
							<div className="mainContent">
								<div className="notificationBody">
									{translator.translate(language, 'my_account', 'MyOrders/details/disclaimerDescriptions/redelivery_address_change_prompt_before_link')}
									<a
										className="interactable"
										style={{ cursor: 'pointer' }}
										onClick={(e) => {
											e.preventDefault();
											setIsRedeliveryCloseable(true);
											setShowRedeliveryAddressPopup(true);
										}}
									>
										{translator.translate(language, 'my_account', 'MyOrders/details/disclaimerDescriptions/redelivery_address_change_prompt_link')}
									</a>
									{translator.translate(language, 'my_account', 'MyOrders/details/disclaimerDescriptions/redelivery_address_change_prompt_after_link')}
								</div>
							</div>
						</div>
					</div>
				)}

				{(() => {
					const pending = data['pendingActions'];
					const isVerificationStatus =
						orderData.status === 'wait_customer_additional_actions' ||
						orderData.status === 'id_verification_in_progress';
					const hasBodyPhotos = !!(pending?.frontPhotoDataUrl || pending?.sidePhotoDataUrl);
					const hasPendingTask = !!(
						pending?.needsPhotoUpload ||
						pending?.needsIdVerification ||
						pending?.idVerificationInProgress ||
						pending?.isWaitingActions
					);
					// Show while verification is open, or anytime body photos exist so the customer can edit them.
					if (!isVerificationStatus && !hasBodyPhotos && !hasPendingTask) return null;
					return (
						<MyAccountPendingActions
							hashID={data['hashID']}
							countryID={country}
							language={language}
							initialPendingData={pending}
							onOrderChanged={() => setRefreshData && setRefreshData((prev: boolean) => !prev)}
						/>
					);
				})()}

		{isProbatixOrder ? (
			<>
			{orderData.hasProbatixPdfAttachment && (
				<MyAccountTestKitResults
					language={language}
					testName={data['title'] || data['shortTitle'] || ''}
					orderHashID={data['hashID']}
					resultsDate={orderData.deliveredAt}
				/>
				)}

				<div className={`account-order-details${isProbatixOrder ? ' probatix-order-details' : ''}`}>
						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/hashID')}
							detailData={data['hashID']}
							language={language}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/dateTime')}
							detailData={data['orderDate']}
							language={language}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/order_type')}
							detailData={translator.translate(language, 'my_account', `MyOrders/details/testKitDetails/order_type_values/${testSampleType}`)}
							language={language}
						/>

					<MyAccountOrderDetailRow
						title={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/test_name')}
						detailData={productsView.length > 0 ? productsView[0].title : data['title']}
						language={language}
						rowID="space-between-row"
					/>

						{orderData.testIncludes && (
							<MyAccountOrderDetailRow
								title={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/test_includes')}
								detailData={orderData.testIncludes}
								language={language}
							/>
						)}

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/collection_method')}
							detailData={translator.translate(language, 'my_account', `MyOrders/details/testKitDetails/collection_method_values/${testSampleType}`)}
							language={language}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/delivery_method')}
							detailData={data['shippingMethod']}
							language={language}
							rowID="space-between-row"
						>
							{data?.orderShipping?.tracking_url && (
								<SubmitButton
									key="shippingMethod"
									id="detail-row-shipping-method"
									placeholder={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/track_shipment')}
									onSubmit={() => {
										window.open(data.orderShipping.tracking_url);
									}}
								/>
							)}
					</MyAccountOrderDetailRow>

				{!isMobile ? (
					<>
					<MyAccountOrderDetailRow
						title={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/total_amount')}
						detailData={probatixTotalPrice}
						language={language}
					/>
					<MyAccountOrderDetailRow
						title={translator.translate(language, 'my_account', 'MyOrders/details/price')}
						detailData={probatixSubPrices}
						language={language}
					>
						{orderActions}
					</MyAccountOrderDetailRow>
					</>
				) : (
						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/total_amount')}
							detailData={data['prices']}
							language={language}
						>
							{orderActions}
						</MyAccountOrderDetailRow>
					)}

					<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/paymentInfo')}
							detailData={paymentMethodWithoutIbanAndSwift}
							language={language}
							additionalData={{ ...data['prices'], hashID: data['hashID'], country: country, isOnlineVD: data.isOnlineVD }}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', orderData.isSelfPickup ? 'MyOrders/details/billingAddress' : 'MyOrders/details/address')}
							detailData={data['address']}
							language={language}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/deliveryAddress')}
							detailData={data['deliveryAddress']}
							language={language}
						/>

						{orderData.resultsURL && (
							<MyAccountOrderDetailRow title={''} detailData={''} language={language}>
								<SubmitButton
									id="view-test-results"
									placeholder={translator.translate(language, 'my_account', 'MyOrders/details/testKitDetails/view_results')}
									onSubmit={() => {
										window.open(orderData.resultsURL!, '_blank');
									}}
								/>
							</MyAccountOrderDetailRow>
						)}
					</div>

					<MyAccountOrderDetailsSupport language={language} country={country} isProbatixOrder={true} orderType="test_kit" />

					<MyAccountOrderNotes
						htmlContent={translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/header')}
						language={language}
						orderStatus={orderData.status}
						testKitInstructions={[
							{
								title: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/order_confirmation_title'),
								content: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/order_confirmation_content'),
							},
							{
								title: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/shipping_title'),
								content: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/shipping_content'),
							},
							{
								title: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/kit_instructions_title'),
								content: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/kit_instructions_content'),
							},
							{
								title: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/cancellation_title'),
								content: translator.translate(language, 'my_account', 'MyOrders/details/testKitInstructions/cancellation_content'),
							},
						]}
					/>
				</>
			) : (
				<>
					<div className={`account-order-details${data?.prices?.planFee ? ' folded-plan-prices' : ''}`}>
						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/hashID')}
							detailData={data['hashID']}
							language={language}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/dateTime')}
							detailData={data['orderDate']}
							language={language}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/details')}
							detailData={`${translator.translate(language, 'my_account', `MyOrders/details/${data.hasPharmacyOnlyMedicine ? 'pharmacy_only' : data['orderType']}`)}
	                          ${data.isCannabisCardRequired ? translator.translate(language, 'my_account', 'MyOrders/details/digital_cannabis_id') : ''}`}
							language={language}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/treatment')}
							detailData={data.orderType == 'prescription_only' ? translator.translate(language, 'my_account', 'MyOrders/CardPrescriptionFor') : productsView}
							language={language}
							rowID="space-between-row"
						/>

						{(orderData.isExternalPharmacy && !EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS.includes(orderData.flow)) ||
						(orderData.isSelfPickup && orderData.status === consts.WAIT_CUSTOMER_PHARMACY_SELECTION) ? (
							<PharmacySelection setOrderData={setOrderData} orderData={orderData} language={language} />
						) : null}

						{shouldShowConsultationDetails && (
							<MyAccountOrderDetailRow
								title={translator.translate(language, 'my_account', 'MyOrders/details/videoConsultation/consultation_details')}
								detailData={""}
								language={language}
							>
								{renderConsultationDetails()}
							</MyAccountOrderDetailRow>
						)}

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/shippingMethod')}
							detailData={
								orderData.isExternalPharmacy
									? cannaleo_pre_selected_flows.includes(orderData.flow)
										? translator.translate(language, 'my_account', 'MyOrders/details/shippingMethodDetailsCannaleoFlow')
										: translator.translate(language, 'my_account', 'MyOrders/details/shippingMethodDetailsExternalPharmacy')
									: getShippingMethodLabel()
							}
							language={language}
							rowID="space-between-row"
						>
							{data?.orderShipping?.tracking_url && data.status === 'sent_to_customer' && (
								<SubmitButton
									key="shippingMethod"
									id="detail-row-shipping-method"
									placeholder={translator.translate(language, 'my_account', `MyOrders/details/trackDelivery`)}
									onSubmit={() => {
										window.open(data.orderShipping.tracking_url);
									}}
								/>
							)}
						</MyAccountOrderDetailRow>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/price')}
							detailData={data['prices']}
							language={language}
						>
							{orderActions}
						</MyAccountOrderDetailRow>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', 'MyOrders/details/paymentInfo')}
							detailData={paymentMethodWithoutIbanAndSwift}
							language={language}
							additionalData={{ ...data['prices'], hashID: data['hashID'], country: country, isOnlineVD: data.isOnlineVD }}
						/>

						<MyAccountOrderDetailRow
							title={translator.translate(language, 'my_account', orderData.isSelfPickup ? 'MyOrders/details/billingAddress' : 'MyOrders/details/address')}
							detailData={data['address']}
							language={language}
						/>

						{data.orderType != 'prescription_only' && !orderData.isSelfPickup && (
							<MyAccountOrderDetailRow
								title={translator.translate(language, 'my_account', 'MyOrders/details/deliveryAddress')}
								detailData={data['deliveryAddress']}
								language={language}
							/>
						)}

						{data.orderType != 'prescription_only' && !isSchengenFlow && !orderData.isProbatixOrder && (
								<MyAccountOrderDetailRow
									title={translator.translate(language, 'my_account', 'MyOrders/details/pharmacyDetails')}
									detailData={data['pharmacyDetails']}
									language={language}
								/>
						)}

						{orderData.isAvailableForReorder ? (
							<MyAccountOrderDetailRow title={''} detailData={''} language={language}>
								<ReorderActions
									orderHashID={data['hashID']}
									country={country}
									language={language}
									reorderClickEvent="Click-Reorder"
									reorderButtonClassName="align-left"
									wlSubscriptionOffer={orderData.wlSubscriptionOffer}
									reorderTrackingContext={orderData.reorderTrackingContext}
									wlReorderVariant={wlReorderVariant}
								/>
							</MyAccountOrderDetailRow>
						) : null}
					</div>
					{data.status === 'delivered' &&
						(!data.haveFeedback ? (
							<OrderFeedbackForm country={country} language={language} orderId={orderData.orderShipping?.order_id} customerID={customer.id} orderData={orderData} />
						) : (
							<ThanksForFeedback country={country} language={language} rating={orderData.feedbackRating} orderData={orderData} />
						))}

					<MyAccountOrderDetailsSupport
						language={language}
						country={country}
						isProbatixOrder={orderData.isProbatixOrder}
						shippingCompanyCode={data['shippingCompanyCode']}
						expressTier={data['expressTier']}
						isSelfPickup={orderData.isSelfPickup}
						isCannabisOrder={orderData.isCannabisOrder}
					/>

					<MyAccountOrderNotes
						htmlContent={translator.translate(language, 'my_account', `MyOrders/details/${orderNotesString}`)}
						language={language}
						orderStatus={orderData.status}
						groupHashID={orderData.groupHashID}
					/>
				</>
			)}
			</div>
		</OrderDetailsContext.Provider>
	);
};

export default MyAccountOrderDetails;
